import React, { useState } from 'react';
import { calculateProgress } from '../../utils/resolutionUtils';
import { useLanguage } from '../../contexts/LanguageContext';
import StatusBadge from '../Common/StatusBadge';
import MetricMeasurementsModal from './MetricMeasurementsModal';

const MetricResolutionUI = ({
	resolution,
	isUpdateAllowed,
	isUpdating,
	onUpdate,
	onDecrypt,
	isDeleteAllowed,
	onDelete,
}) => {
	const { t } = useLanguage();
	const [measurementInput, setMeasurementInput] = useState('');
	const [noteInput, setNoteInput] = useState('');

	const latestMeasurement =
		resolution.measurements && resolution.measurements.length
			? resolution.measurements[resolution.measurements.length - 1]
			: null;

	const [isMeasurementsOpen, setIsMeasurementsOpen] = useState(false);

	const handleAddMeasurement = () => {
		if (measurementInput === '') return;
		const val = parseFloat(measurementInput);
		if (isNaN(val)) return;

		const newMeasurements = [
			...(resolution.measurements || []),
			{ date: new Date().toISOString(), value: val, note: noteInput || '' },
		];
		// add currentValue to resolution
		const updated = { ...resolution, measurements: newMeasurements, currentValue: val };
		onUpdate(resolution.id, updated, t);
		setMeasurementInput('');
		setNoteInput('');
	};

	const progress = calculateProgress(resolution);

	return (
		<div className='bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow'>
			<div className='mb-3'>
				<h3 className='text-lg font-semibold text-gray-900 mb-1'>{resolution.text}</h3>
				{resolution.isEncrypted && (
					<span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800'>
						🔒 {t('private')}
					</span>
				)}
			</div>

			<div className='mb-4'>
				<div>
					<div className='flex justify-between items-center mb-2'>
						<span className='text-sm text-gray-600'>{t('progress')}</span>
						<div className='flex items-center space-x-2'>
							<span className='text-sm font-medium'>{progress}%</span>
							<button
								onClick={() => setIsMeasurementsOpen(true)}
								className='p-1 rounded-md text-sm bg-gray-100 hover:bg-gray-200'
								title={t('measurements')}
							>
								📊
							</button>
						</div>
					</div>
					<div className='w-full bg-gray-200 rounded-full h-2'>
						<div
							className='bg-blue-600 h-2 rounded-full transition-all duration-300'
							style={{ width: `${Math.min(progress, 100)}%` }}
						></div>
					</div>
					<div className='text-right mt-1'>
						<span className='text-xs text-gray-500'>
							{latestMeasurement
								? `${latestMeasurement.value} ${resolution.unit || ''}`
								: `${resolution.startValue || '-'} ${resolution.unit || ''}`}
						</span>
					</div>
				</div>
			</div>

			<div className='mb-4'>
				<StatusBadge statusCode={resolution.statusCode} status={t(resolution.statusCode)} />
			</div>

			<div className='flex items-center justify-between'>
				<div className='flex items-center space-x-2'>
					{isUpdateAllowed && resolution.statusCode !== 'completed' && (
						<div className='flex items-center space-x-2'>
							<input
								type='number'
								step='any'
								value={measurementInput}
								onChange={(e) => setMeasurementInput(e.target.value)}
								className='border border-gray-300 rounded-md p-2 text-sm w-28'
								placeholder={t('value')}
							/>
							<input
								type='text'
								value={noteInput}
								onChange={(e) => setNoteInput(e.target.value)}
								className='border border-gray-300 rounded-md p-2 text-sm w-36'
								placeholder={t('note')}
							/>
							<button
								onClick={handleAddMeasurement}
								disabled={isUpdating}
								className={`px-3 py-1 text-white rounded-md text-sm ${
									isUpdating ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'
								}`}
							>
								{t('add')}
							</button>
						</div>
					)}

					{resolution.isEncrypted && (
						<button
							onClick={() => onDecrypt(resolution.id)}
							className='px-3 py-1 bg-yellow-500 text-white rounded-md text-sm hover:bg-yellow-600'
							title='Decrypt resolution'
						>
							🔓 {t('decrypt')}
						</button>
					)}
				</div>
				{isDeleteAllowed && (
					<button
						onClick={() => onDelete(resolution.id, t)}
						className='px-3 py-1 bg-red-500 text-white rounded-md text-sm hover:bg-red-600'
						title={t('delete')}
					>
						🗑️ {t('delete')}
					</button>
				)}
			</div>
			{/* Measurements Modal */}
			<MetricMeasurementsModal
				isOpen={isMeasurementsOpen}
				onClose={() => setIsMeasurementsOpen(false)}
				measurements={resolution.measurements || []}
				unit={resolution.unit}
			/>
		</div>
	);
};

export default MetricResolutionUI;
